/**
 * Клиент public-api.
 *
 * Перенесён из sharing-admin/src/lib/http.ts с тремя отличиями:
 *  1. токены асинхронные (см. tokens.ts) — до гидратации запрос не идёт;
 *  2. добавлен X-Idempotency-Key: BFF пробрасывает его в домен
 *     (.claude/rules/context-propagation.md), это то, что делает досылку
 *     из офлайн-очереди безопасной;
 *  3. сетевой сбой отличается от ответа сервера — офлайн-очередь должна
 *     повторять только первое.
 *
 * Соглашения бэкенда (grpc-gateway), нарушать нельзя:
 *  - camelCase, не snake_case: pageSize, createdAt, batteryPct;
 *  - int64 приходит СТРОКОЙ: "amountMinor": "88000";
 *  - нулевые поля опускаются ⇒ у каждого поля схемы обязан быть .default(...);
 *  - время — RFC3339 UTC, пустая строка = «не задано»;
 *  - ошибки Kratos: { code, reason, message, metadata }.
 */
import { z } from 'zod'
import {
  clearTokens,
  isExpired,
  isHydrated,
  readTokens,
  writeTokens,
  type TokenPair,
} from './tokens'

export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api'

/** Ошибка Kratos: { code, reason, message, metadata }. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly reason: string,
    message: string,
    readonly metadata: Record<string, string> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isClient(): boolean {
    return this.status >= 400 && this.status < 500
  }
}

/**
 * Сеть не ответила вовсе (нет связи, таймаут, DNS).
 *
 * Отдельный класс, потому что офлайн-очередь обязана различать «сервер
 * отказал» (повтор бессмыслен, действие надо показать скауту) и «связи не
 * было» (повторить, когда появится). Выдумывать для второго случая
 * ApiError.status было бы враньём.
 */
export class NetworkError extends Error {
  constructor(readonly reasonCause?: unknown) {
    super('Нет связи с сервером')
    this.name = 'NetworkError'
  }
}

/**
 * Ответа не дождались за `timeoutMs`.
 *
 * Наследник `NetworkError`, потому что для повтора это то же самое: запрос
 * мог дойти и сработать, повторять — только с тем же ключом идемпотентности,
 * а очередь его повторит. Свой класс — ради текста: «нет связи» здесь неправда.
 */
export class RequestTimeoutError extends NetworkError {
  constructor() {
    super()
    this.name = 'RequestTimeoutError'
  }
}

const kratosError = z.object({
  code: z.number().optional(),
  reason: z.string().optional(),
  message: z.string().optional(),
  metadata: z.record(z.string(), z.string()).optional(),
})

/**
 * Обновление токенов идёт ОДНИМ промисом на всё приложение.
 *
 * Бэкенд детектирует повторное использование refresh-токена и отзывает всю
 * семью (401 REFRESH_REUSED), включая только что выданный. Два параллельных
 * 401 без этой блокировки мгновенно выкидывают скаута из системы — а он в
 * этот момент стоит у сломанного байка.
 */
let refreshInFlight: Promise<TokenPair | null> | null = null

type Listener = () => void
const sessionEndedListeners = new Set<Listener>()

export function onSessionEnded(fn: Listener): () => void {
  sessionEndedListeners.add(fn)
  return () => {
    sessionEndedListeners.delete(fn)
  }
}

function announceSessionEnded(): void {
  clearTokens()
  sessionEndedListeners.forEach((fn) => {
    fn()
  })
}

async function doRefresh(refreshToken: string): Promise<TokenPair | null> {
  const res = await fetch(`${API_BASE}/v1/iam/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })
  if (!res.ok) return null
  const body = (await res.json()) as Partial<TokenPair>
  if (!body.accessToken || !body.refreshToken) return null
  const pair: TokenPair = {
    accessToken: body.accessToken,
    refreshToken: body.refreshToken,
    expiresAt: body.expiresAt ?? '',
  }
  writeTokens(pair)
  return pair
}

export function refreshTokens(): Promise<TokenPair | null> {
  if (refreshInFlight) return refreshInFlight
  const current = readTokens()
  if (!current?.refreshToken) return Promise.resolve(null)

  refreshInFlight = doRefresh(current.refreshToken)
    .catch(() => null)
    .finally(() => {
      refreshInFlight = null
    })
  return refreshInFlight
}

/** Тесты сбрасывают общий промис между кейсами. */
export function __resetRefreshState(): void {
  refreshInFlight = null
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  /** Запрос без токена — только запрос кода и обновление. */
  anonymous?: boolean
  /**
   * Потолок ожидания ответа, мс. По умолчанию его нет: команды замку по
   * сотовой сети отвечают долго. Ставит тот, кто знает, сколько ждёт сервер.
   */
  timeoutMs?: number
  /**
   * Ключ идемпотентности. Обязателен для всего, что уходит из офлайн-очереди:
   * повторная доставка не должна порождать вторую задачу или второе списание.
   */
  idempotencyKey?: string
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    params.set(k, String(v))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function readError(res: Response): Promise<ApiError> {
  let reason = 'UNKNOWN'
  let message = `Запрос не выполнен (${res.status})`
  let metadata: Record<string, string> = {}
  try {
    const parsed = kratosError.safeParse(await res.json())
    if (parsed.success) {
      reason = parsed.data.reason ?? reason
      message = parsed.data.message ?? message
      metadata = parsed.data.metadata ?? {}
    }
  } catch {
    /* тело не JSON — оставляем дефолтный текст */
  }
  return new ApiError(res.status, reason, message, metadata)
}

async function fire(path: string, opts: RequestOptions, token?: string): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`
  if (opts.idempotencyKey) headers['X-Idempotency-Key'] = opts.idempotencyKey

  // exactOptionalPropertyTypes: явный `undefined` в RequestInit запрещён,
  // поэтому необязательные поля добавляются, а не присваиваются как undefined.
  const init: RequestInit = { method: opts.method ?? 'GET', headers }
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body)
  if (opts.signal) init.signal = opts.signal

  // Свой контроллер, а не AbortSignal.timeout: его нет в старых WebView, и
  // отличить «потолок» от чужой отмены надёжнее флагом, чем именем ошибки.
  let timedOut = false
  let timer: ReturnType<typeof setTimeout> | undefined
  if (opts.timeoutMs !== undefined) {
    const ctrl = new AbortController()
    opts.signal?.addEventListener('abort', () => {
      ctrl.abort()
    })
    timer = setTimeout(() => {
      timedOut = true
      ctrl.abort()
    }, opts.timeoutMs)
    init.signal = ctrl.signal
  }

  try {
    return await fetch(buildUrl(path, opts.query), init)
  } catch (e) {
    if (timedOut) throw new RequestTimeoutError()
    // fetch отвергает промис только когда запрос не состоялся вовсе.
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw new NetworkError(e)
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Запрос к public-api. Обновляет токен заранее, если он вот-вот протухнет,
 * и ровно один раз повторяет запрос после 401.
 */
export async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  opts: RequestOptions = {},
): Promise<T> {
  let token: string | undefined

  if (!opts.anonymous) {
    if (!isHydrated()) {
      throw new ApiError(401, 'NOT_HYDRATED', 'Сессия ещё не загружена')
    }
    const pair = readTokens()
    if (!pair) {
      announceSessionEnded()
      throw new ApiError(401, 'NO_SESSION', 'Сессия не найдена. Войдите заново.')
    }
    token = isExpired(pair.accessToken) ? (await refreshTokens())?.accessToken : pair.accessToken
    if (!token) {
      announceSessionEnded()
      throw new ApiError(401, 'SESSION_EXPIRED', 'Сессия истекла. Войдите заново.')
    }
  }

  let res = await fire(path, opts, token)

  if (res.status === 401 && !opts.anonymous) {
    const renewed = await refreshTokens()
    if (!renewed) {
      announceSessionEnded()
      throw await readError(res)
    }
    res = await fire(path, opts, renewed.accessToken)
    if (res.status === 401) {
      announceSessionEnded()
      throw await readError(res)
    }
  }

  if (!res.ok) throw await readError(res)
  if (res.status === 204) return schema.parse({})

  const text = await res.text()
  const raw: unknown = text.length ? JSON.parse(text) : {}

  const parsed = schema.safeParse(raw)
  if (!parsed.success) {
    // Схема разошлась с ответом бэкенда — это баг, который нельзя прятать
    // за пустым экраном: скаут решит, что задач нет, и уедет.
    console.error('[api] ответ не соответствует схеме', path, parsed.error.issues, raw)
    throw new ApiError(
      res.status,
      'SCHEMA_MISMATCH',
      `Ответ ${path} не соответствует ожидаемому формату`,
    )
  }
  return parsed.data
}
